import React, { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { SpinnerIcon } from '@components-library/Icons/iconsComponents';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { Button as AntdButton, ButtonProps } from 'antd';
import cn from 'classnames';
import styles from './buttonNew.module.scss';

export type ButtonNewType =
  | 'primary'
  | 'secondary'
  | 'link'
  | 'default'
  | 'primaryMember'
  | 'secondaryMember'
  | 'defaultMember'
  | 'linkMember'
  | 'grayMember';

export interface IButtonNewProps {
  block?: boolean;
  disabled?: boolean;
  type?: ButtonNewType;
  size?: ButtonProps['size'];
  isLoading?: boolean;
  isActive?: boolean;
  children?: React.ReactNode | string;
  icon?: React.ReactNode;
  onClick?: ButtonProps['onClick'];
  className?: string;
  classes?: {
    text?: string;
  };
  alignSelf?: 'flex-start' | 'flex-end' | 'center';
  tooltip?: string;
  target?: string;
  htmlType?: ButtonProps['htmlType'];
}

export const ButtonNew = React.forwardRef(
  (
    {
      type = 'default',
      isLoading,
      isActive,
      size,
      block,
      disabled,
      children,
      icon,
      className,
      classes,
      alignSelf,
      tooltip,
      htmlType,
      onClick,
    }: IButtonNewProps,
    ref,
  ) => {
    const { i18n } = useTranslation();

    const isText = (children: React.ReactNode | string): children is string => {
      return typeof children === 'string';
    };

    const loadingIcon = isLoading ? <SpinnerIcon className={styles.spinner} size={size === 'small' ? 16 : 20} /> : null;

    const customTypeToAntdType: Record<typeof type, ButtonProps['type']> = {
      primary: 'primary',
      secondary: 'primary',
      link: 'link',
      default: 'default',

      primaryMember: 'primary',
      secondaryMember: 'primary',
      linkMember: 'link',
      defaultMember: 'default',
      grayMember: 'default',
    };

    const renderIcon = () => {
      if (!icon) {
        return undefined;
      }

      if (!children && isLoading) {
        return loadingIcon;
      }

      return <span className={cn(styles.iconContainer, { [styles.loading]: isLoading })}>{icon}</span>;
    };

    return (
      <AntdButton
        ref={ref as Ref<HTMLElement>}
        title={tooltip && i18n.t(tooltip as KeyMapKeys)}
        onClick={(e) => {
          if (isLoading) {
            return;
          }
          onClick?.(e);
        }}
        className={cn(className, styles.newButton, {
          [styles.disabled]: disabled,
          [styles.active]: isActive,
          [styles.default]: type === 'default',
          [styles.primary]: type === 'primary',
          [styles.secondary]: type === 'secondary',
          [styles.link]: type === 'link',
          [styles.primaryMember]: type === 'primaryMember',
          [styles.secondaryMember]: type === 'secondaryMember',
          [styles.defaultMember]: type === 'defaultMember',
          [styles.linkMember]: type === 'linkMember',
          [styles.grayMember]: type === 'grayMember',
          [styles.alignSelfStart]: alignSelf === 'flex-start',
          [styles.alignSelfEnd]: alignSelf === 'flex-end',
          [styles.alignSelfCenter]: alignSelf === 'center',
          [styles.large]: size === 'large',
        })}
        size={size}
        block={block}
        icon={renderIcon()}
        htmlType={htmlType}
        type={customTypeToAntdType[type]}
        disabled={disabled}>
        {Boolean(children) && (
          <div className={styles.textAndLoaderContainer}>
            <div className={styles.loadingContainer}>{loadingIcon}</div>
            <div className={cn(classes?.text, styles.text, { [styles.loading]: isLoading })}>
              {isText(children) ? i18n.t(children as KeyMapKeys) : children}
            </div>
          </div>
        )}
      </AntdButton>
    );
  },
);
